import type {
  APIResponse,
  EmployerCandidateDirectoryDetail,
  EmployerCandidateDirectoryListResult,
  EmployerCandidateDirectoryOutreachDraftInput,
  EmployerCandidateDirectoryOutreachDraftResult,
  EmployerCandidateDirectoryOutreachInput,
  EmployerCandidateDirectoryOutreachResult,
  EmployerCandidateDirectorySaveResult,
  EmployerCandidateDirectorySearchInput,
  ParseCandidateSearchResult,
} from '@tabbio/types';
import { api } from './api';

export class EmployerCandidatesApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly meta?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'EmployerCandidatesApiError';
  }
}

function getEmployerCandidatesResponseError<T>(response: APIResponse<T>) {
  if (!response.error) {
    return null;
  }

  return new EmployerCandidatesApiError(
    response.error.message,
    response.error.code,
    response.meta as Record<string, unknown> | undefined,
  );
}

function appendArrayParam(
  params: URLSearchParams,
  key: string,
  values?: string[],
) {
  if (!values?.length) {
    return;
  }

  params.set(key, values.join(','));
}

export async function fetchEmployerCandidates(
  input: EmployerCandidateDirectorySearchInput = {},
): Promise<EmployerCandidateDirectoryListResult> {
  const params = new URLSearchParams();
  if (input.q?.trim()) params.set('q', input.q.trim());
  if (input.cursor) params.set('cursor', input.cursor);
  if (input.limit) params.set('limit', String(input.limit));
  if (input.spotlight) params.set('spotlight', input.spotlight);
  if (input.jobId?.trim()) params.set('jobId', input.jobId.trim());
  appendArrayParam(params, 'locations', input.locations);
  appendArrayParam(params, 'skills', input.skills);
  appendArrayParam(params, 'companies', input.companies);

  const response = await api.get<EmployerCandidateDirectoryListResult>(
    `/api/employer/candidates${params.size ? `?${params.toString()}` : ''}`,
  );
  const error = getEmployerCandidatesResponseError(response);
  if (error) throw error;

  return (
    response.data ?? {
      candidates: [],
      total: 0,
      nextCursor: null,
      searchSessionId: null,
    }
  );
}

export async function fetchEmployerCandidateDetail(
  candidateUserId: string,
  searchSessionId?: string | null,
): Promise<EmployerCandidateDirectoryDetail> {
  const params = new URLSearchParams();
  if (searchSessionId) {
    params.set('searchSessionId', searchSessionId);
  }

  const response = await api.get<EmployerCandidateDirectoryDetail>(
    `/api/employer/candidates/${candidateUserId}${params.size ? `?${params.toString()}` : ''}`,
  );
  const error = getEmployerCandidatesResponseError(response);
  if (error) throw error;
  if (!response.data) throw new Error('Candidate not found');
  return response.data;
}

export async function saveEmployerCandidate(
  candidateUserId: string,
): Promise<EmployerCandidateDirectorySaveResult> {
  const response = await api.post<EmployerCandidateDirectorySaveResult>(
    `/api/employer/candidates/${candidateUserId}/save`,
    {},
  );
  const error = getEmployerCandidatesResponseError(response);
  if (error) throw error;
  if (!response.data) throw new Error('Candidate save returned no data');
  return response.data;
}

export async function sendEmployerCandidateOutreach(
  candidateUserId: string,
  input: EmployerCandidateDirectoryOutreachInput,
): Promise<EmployerCandidateDirectoryOutreachResult> {
  const response = await api.post<EmployerCandidateDirectoryOutreachResult>(
    `/api/employer/candidates/${candidateUserId}/outreach`,
    input,
  );
  const error = getEmployerCandidatesResponseError(response);
  if (error) throw error;
  if (!response.data) throw new Error('Candidate outreach returned no data');
  return response.data;
}

export async function parseEmployerCandidateSearch(
  query: string,
): Promise<ParseCandidateSearchResult> {
  const response = await api.post<ParseCandidateSearchResult>(
    '/api/employer/candidates/parse-search',
    { query },
  );
  const error = getEmployerCandidatesResponseError(response);
  if (error) throw error;
  return (
    response.data ?? {
      q: query,
      locations: [],
      skills: [],
      companies: [],
      unmatchedTokens: [],
    }
  );
}

export async function draftEmployerCandidateOutreach(
  candidateUserId: string,
  input: EmployerCandidateDirectoryOutreachDraftInput = {},
): Promise<EmployerCandidateDirectoryOutreachDraftResult> {
  const response = await api.post<EmployerCandidateDirectoryOutreachDraftResult>(
    `/api/employer/candidates/${candidateUserId}/outreach/draft`,
    input,
  );
  const error = getEmployerCandidatesResponseError(response);
  if (error) throw error;
  if (!response.data) throw new Error('Candidate outreach draft returned no data');
  return response.data;
}
